import { Box, createStyles, LoadingOverlay } from '@mantine/core';
import React, { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import {
  useConfig,
  useFeatureFlags,
  usePortalConfig,
} from '@portals/api/partners';
import { useCurrentUser } from '@portals/api/ui';
import {
  getChildRoutes,
  HubsNavbar,
  HubsSidebar,
  isPartnerFeatureOn,
  Main,
  Modals,
  Navbar,
  Page404,
  QuickNavProvider,
  Sidebar,
  useHasSupportSeat,
  usePermissionAccess,
} from '@portals/framework';
import { useIsSidebarOpen } from '@portals/redux';

import { routes } from '../../routes';

export function DashboardLayout() {
  const config = useConfig();
  const portalConfig = usePortalConfig();
  const currentUser = useCurrentUser();
  const featureFlags = useFeatureFlags();

  const hasSupportSeat = useHasSupportSeat();
  const { isMissingAllPermissions, isSuperAdmin, isAdmin, canView, canEdit } =
    usePermissionAccess();

  const isFetched =
    config.isFetched && portalConfig.isFetched && currentUser.isFetched;

  const hasNoPermissions =
    isMissingAllPermissions && !isSuperAdmin && !hasSupportSeat;

  const isHubsEnabled = isPartnerFeatureOn(featureFlags, 'hubs');

  if (!isFetched) {
    return <LoadingOverlay visible />;
  }

  if (config.data?.partner?.onboarding_step !== 'complete') {
    return <Navigate to="/onboarding" replace />;
  }

  const dashboardRoutes: ReactNode[] = [];
  routes.dashboard.forEach((dashboardRoute) => {
    if (
      dashboardRoute.canAccessRoute &&
      !dashboardRoute.canAccessRoute({
        configuration: config.data,
        canView,
        canEdit,
        isAdmin,
        hasSupportSeat,
      })
    ) {
      return;
    }

    dashboardRoutes.push(
      getChildRoutes({
        routes: dashboardRoute.childRoutes,
        configuration: config.data,
        canView,
        canEdit,
        isAdmin,
        hasSupportSeat,
      })
    );
  });

  routes.routeModals?.forEach((modalRoute) => {
    if (
      modalRoute.canAccessRoute &&
      !modalRoute.canAccessRoute({
        configuration: config.data,
        canView,
        canEdit,
        isAdmin,
        hasSupportSeat,
      })
    ) {
      return;
    }

    dashboardRoutes.push(
      getChildRoutes({
        routes: [modalRoute],
        configuration: config.data,
        canView,
        canEdit,
        isAdmin,
        hasSupportSeat,
      })
    );
  });

  routes.routePanels?.forEach((panelRoute) => {
    if (
      panelRoute.canAccessRoute &&
      !panelRoute.canAccessRoute({
        configuration: config.data,
        canView,
        canEdit,
        isAdmin,
        hasSupportSeat,
      })
    ) {
      return;
    }

    dashboardRoutes.push(
      getChildRoutes({
        routes: [panelRoute],
        configuration: config.data,
        canView,
        canEdit,
        isAdmin,
        hasSupportSeat,
      })
    );
  });

  return (
    <QuickNavProvider>
      {isHubsEnabled ? (
        <HubsLayout hasNoPermissions={hasNoPermissions}>
          <Routes>
            {dashboardRoutes}

            <Route path="*" element={<Page404 />} />
          </Routes>
        </HubsLayout>
      ) : (
        <LegacyDashboardLayout hasNoPermissions={hasNoPermissions}>
          <Routes>
            {dashboardRoutes}

            <Route path="*" element={<Page404 />} />
          </Routes>
        </LegacyDashboardLayout>
      )}

      <Modals />
    </QuickNavProvider>
  );
}

function HubsLayout({
  children,
  hasNoPermissions,
}: {
  children: ReactNode;
  hasNoPermissions: boolean;
}) {
  const isSidebarOpen = useIsSidebarOpen();
  const { classes, cx } = useStyles(isSidebarOpen);

  return (
    <Box className={cx(classes.wrapper, 'page-wrapper', 'hubs-enabled')}>
      <HubsNavbar />

      <Main isHubsEnabled>
        <HubsSidebar />

        <div id="page-content" className={cx(classes.content, 'page-content')}>
          {hasNoPermissions ? (
            <Page404
              hideActionButton
              hideImage
              title="Missing permissions"
              description="We're sorry, it looks like you don't have the necessary permissions to access this page. Please reach out to your administrator for assistance."
            />
          ) : (
            <>{children}</>
          )}
        </div>
      </Main>
    </Box>
  );
}

function LegacyDashboardLayout({
  hasNoPermissions,
  children,
}: {
  hasNoPermissions: boolean;
  children: ReactNode;
}) {
  const isSidebarOpen = useIsSidebarOpen();
  const { classes, cx } = useStyles(isSidebarOpen);

  return (
    <Box className={cx(classes.wrapper, 'page-wrapper')}>
      <Sidebar />

      <Main>
        <Navbar />
        <div id="page-content" className={cx(classes.content, 'page-content')}>
          {hasNoPermissions ? (
            <Page404
              hideActionButton
              hideImage
              title="Missing permissions"
              description="We're sorry, it looks like you don't have the necessary permissions to access this page. Please reach out to your administrator for assistance."
            />
          ) : (
            <>{children}</>
          )}
        </div>
      </Main>
    </Box>
  );
}

const useStyles = createStyles((theme, isSidebarOpen: boolean) => ({
  wrapper: {
    height: '100vh',
    overflow: 'hidden',
    display: 'grid',

    // TODO: Hubs layout (also remove the :not line when removing hubs)
    '&.hubs-enabled': {
      gridTemplateRows: `${theme.other.navbarHeight}px 1fr`,
      gridTemplateColumns: 'unset',
    },
    // TODO: Hubs layout

    '&:not(.hubs-enabled)': {
      gridTemplateColumns: `${isSidebarOpen ? 300 : 76}px 1fr`,

      [theme.fn.smallerThan('xl')]: {
        gridTemplateColumns: `${isSidebarOpen ? 250 : 76}px 1fr`,
      },
    },
  },
  content: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
  },
}));
